import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MessageService } from 'primeng/api';
import { PrimeNgModule } from '@shared/prime-ng/prime-ng.module';
import { HcpcsCodeItemDTO } from '@core/models';
import { IFilterDropdownsForm } from '@core/models/dropdowns.model';
import { SaveHcpcsRequestDTO, SaveHcpcsResult } from '@core/models';
import { CodeInformationComponent } from '../code-information/code-information.component';
import { ModifiersComponent } from '../modifiers/modifiers.component';
import { ClinicalQuestionnaireFormsComponent } from '../clinical-questionnaire-forms/clinical-questionnaire-forms.component';
import { CodeManagementApiService } from '@core/services/code-management-api.service';
import { DateService } from '@core/services/date.service';
import { SecurityModule } from '@mynexus/mynexus-ui-lib';

@Component({
  selector: 'app-code-expanded-view',
  templateUrl: './code-expanded-view.component.html',
  styleUrls: ['./code-expanded-view.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    PrimeNgModule,
    CodeInformationComponent,
    ModifiersComponent,
    ClinicalQuestionnaireFormsComponent,
    SecurityModule
  ],
  providers: [MessageService]
})
export class CodeExpandedViewComponent implements OnInit {
  @Input() code!: HcpcsCodeItemDTO;
  @Input() filters!: IFilterDropdownsForm | null;
  
  @ViewChild(CodeInformationComponent) codeInfoComponent!: CodeInformationComponent;
  @ViewChild(ModifiersComponent) modifiersComponent!: ModifiersComponent;
  @ViewChild(ClinicalQuestionnaireFormsComponent) clinicalFormsComponent!: ClinicalQuestionnaireFormsComponent;
  
  modifiersData: any[] = [];
  clinicalFormsData: any[] = [];
  isLoadingModifiers = false;
  isLoadingClinicalForms = false;
  isLoading = true;
  codeDetails: any = null;
  codeDataWithCmsDates: any = null;
  isHcpcsCodeExists: boolean = false;
  
  planEffectiveStartDate: Date | null = null;
  planEffectiveEndDate: Date | null = null;
  cmsEffectiveStartDate: Date | null = null;
  cmsEffectiveEndDate: Date | null = null;
  
  isEditMode = false;
  showDiscardDialog = false;
  showNoFormsDialog = false;
  isSaving = false;
  initialCodeInfoData: any = null;

  constructor(
    private codeManagementApiService: CodeManagementApiService,
    private dateService: DateService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.updateCodeData();
    
    if (this.code?.hcpcsId) {
      this.loadCodeDetails();
    }
    
    if (this.code?.hcpcsId && this.filters) {
      this.loadModifiersData();
    }
  }
  
  private updateCodeData(): void {
    if (!this.code) return;
    
    this.codeDataWithCmsDates = {
      ...this.code,
      cmsEffectiveStartDate: this.cmsEffectiveStartDate,
      cmsEffectiveEndDate: this.cmsEffectiveEndDate,
      program: this.filters?.productId || '',
      healthPlan: this.filters?.tenantId || '',
      planType: this.filters?.overrideTypeId || '',
      planEffectiveStartDate: this.planEffectiveStartDate,
      planEffectiveEndDate: this.planEffectiveEndDate,
      isHcpcsCodeExists: this.isHcpcsCodeExists
    };
    
  }
    
  private loadCodeDetails(): void {
    this.codeManagementApiService.getCodeDetails(this.code.hcpcsId).subscribe({
      next: (details) => {
        this.codeDetails = details;
        
        this.cmsEffectiveStartDate = this.parseDateValue(details.cmsEffectiveStartDate);
        this.cmsEffectiveEndDate = this.parseDateValue(details.cmsEffectiveEndDate);
        
        this.updateCodeData();
      }
    });
  }

  private loadModifiersData(updateGrid: boolean = false): void {
    if (!this.filters) {
      return;
    }

    const hcpcsId = this.code.hcpcsId;
    const productId = this.filters.productId;
    const tenantId = this.filters.tenantId;
    const overrideTypeId = this.filters.overrideTypeId;

    if (!hcpcsId || !productId || !tenantId || !overrideTypeId) {
      return;
    }

    this.isLoadingModifiers = true;
    this.isLoadingClinicalForms = true;
    this.codeManagementApiService.getCodeAssociations(hcpcsId, productId, tenantId, overrideTypeId).subscribe({
      next: (associations) => {
        this.isHcpcsCodeExists = associations.isHcpcsCodeExists || false;        
        this.planEffectiveStartDate = this.parseDateValue(associations.planEffectiveStartDate);
        this.planEffectiveEndDate = this.parseDateValue(associations.planEffectiveEndDate);
        
        if (associations.hcpcsModifiers && associations.hcpcsModifiers.length > 0) {
          this.modifiersData = associations.hcpcsModifiers;
        } else {
          this.modifiersData = [];
        }
        
        if (associations.hcpcsClinicalForms && associations.hcpcsClinicalForms.length > 0) {
          this.clinicalFormsData = associations.hcpcsClinicalForms.map(form => ({
            id: form.hcpcsToFormId,
            associatedFormId: form.formIoId,
            associatedFormTitle: form.formIoTitle,
            sharedForm: form.isSharedForm ? 'Yes' : 'No',
            formEffectiveStartDate: this.parseDateValue(form.effectiveStartDate),
            isNew: false,
            isNewClinicalForm: false,
            isEditing: false,
            isFormDateLocked: true
          }));
        } else {
          this.clinicalFormsData = [];
        }
        
        if (updateGrid) {
          this.refreshExpandedCodeGrid(associations);
        }
        
        this.updateCodeData();        
        this.isLoadingModifiers = false;
        this.isLoadingClinicalForms = false;
        this.isLoading = false;
      },
      error: () => {
        this.isLoadingModifiers = false;
        this.isLoadingClinicalForms = false;
        this.isLoading = false;
        this.modifiersData = [];
        this.clinicalFormsData = [];
      }
    });
  }
  
  toggleEditMode(): void {
    if (!this.isEditMode) {
      this.isEditMode = true;
      this.saveInitialCodeInformation();
      this.codeInfoComponent?.codeInfoForm?.markAsPristine();
    }
  }
  
  onCodeInfoChange(data: any): void {
    if ('cmsEffectiveStartDate' in data) {
      this.cmsEffectiveStartDate = data.cmsEffectiveStartDate;
    }
    if ('cmsEffectiveEndDate' in data) {
      this.cmsEffectiveEndDate = data.cmsEffectiveEndDate;
    }
    if ('planEffectiveStartDate' in data) {
      this.planEffectiveStartDate = data.planEffectiveStartDate;
    }
    if ('planEffectiveEndDate' in data) {
      this.planEffectiveEndDate = data.planEffectiveEndDate;
    }
  }
  
  private saveInitialCodeInformation(): void {
    this.initialCodeInfoData = {
      codeDataWithCmsDates: JSON.parse(JSON.stringify(this.codeDataWithCmsDates)),
      modifiersData: JSON.parse(JSON.stringify(this.modifiersData)),
      clinicalFormsData: JSON.parse(JSON.stringify(this.clinicalFormsData)),
      modifiersApiData: JSON.parse(JSON.stringify(this.modifiersComponent?.getModifiersData() || [])),
      clinicalFormsApiData: JSON.parse(JSON.stringify(this.clinicalFormsComponent?.getFormsData() || [])),
      planEffectiveStartDate: this.planEffectiveStartDate,
      planEffectiveEndDate: this.planEffectiveEndDate
    };
  }
  
  get canSaveCodeChanges(): boolean {
    if (!this.validateAllForms()) return false;
    
    const codeInfoChanged = this.codeInfoComponent?.codeInfoForm?.dirty ?? false;
    
    const currentModifiers = this.modifiersComponent?.getModifiersData() || [];
    const modifiersChanged = JSON.stringify(currentModifiers) !== JSON.stringify(this.initialCodeInfoData?.modifiersApiData || []);
    
    const currentClinicalForms = this.clinicalFormsComponent?.getFormsData() || [];
    const clinicalFormsChanged = JSON.stringify(currentClinicalForms) !== JSON.stringify(this.initialCodeInfoData?.clinicalFormsApiData || []);
    
    return codeInfoChanged || modifiersChanged || clinicalFormsChanged;
  }
  
  onDiscardChanges(): void {
    this.showDiscardDialog = true;
  }
  
  confirmDiscard(): void {
    if (this.initialCodeInfoData) {
      this.modifiersData = JSON.parse(JSON.stringify(this.initialCodeInfoData.modifiersData));
      this.clinicalFormsData = JSON.parse(JSON.stringify(this.initialCodeInfoData.clinicalFormsData));
      this.planEffectiveStartDate = this.initialCodeInfoData.planEffectiveStartDate;
      this.planEffectiveEndDate = this.initialCodeInfoData.planEffectiveEndDate;
      this.updateCodeData();
    }
    this.isEditMode = false;
    this.showDiscardDialog = false;
    this.initialCodeInfoData = null;
  }
  
  cancelDiscard(): void {
    this.showDiscardDialog = false;
  }
  
  cancelNoFormsDialog(): void {
    this.showNoFormsDialog = false;
  }
  
  proceedWithDefaultForm(): void {
    this.showNoFormsDialog = false;
    
    if (this.clinicalFormsComponent) {
      this.clinicalFormsComponent.addDefaultFormIfNeeded();
    }
  }
  
  onSaveChanges(): void {
    if (!this.validateAllForms()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields'
      });
      return;
    }
    
    const clinicalForms = this.clinicalFormsComponent?.getFormsData() || [];
    if (clinicalForms.length === 0) {
      this.showNoFormsDialog = true;
      return;
    }
    
    this.saveCode();
  }
  
  private validateAllForms(): boolean {
    const codeInfoValid = this.codeInfoComponent?.isValid ?? false;
    if (!codeInfoValid) return false;
    
    const hasEditingModifiers = this.modifiersComponent?.modifiersList?.controls.some(
      control => control.get('isEditing')?.value === true
    ) ?? false;
    if (hasEditingModifiers) return false;
    
    const hasEditingForms = this.clinicalFormsComponent?.formsList?.controls.some(
      control => control.get('isEditing')?.value === true
    ) ?? false;
    if (hasEditingForms) return false;
    
    return true;
  }
  
  private saveCode(): void {
    if (this.isSaving || !this.filters) {
      return;
    }
    
    this.isSaving = true;
    
    const formData = this.codeInfoComponent.getFormValue();
    const clinicalForms = this.clinicalFormsComponent?.getFormsData() || [];
    const modifiers = this.modifiersComponent?.getModifiersData() || [];
    
    const formatDateForAPI = (date: any): string | null => {
      if (!date) return null;
      return this.dateService.transformPOSTDate(date);
    };
    
    const request: SaveHcpcsRequestDTO = {
      hcpcsId: this.code.hcpcsId,
      hcpcsCode: this.code.code,
      shortDescription: formData.shortDescription,
      cmsEffectiveStartDate: formatDateForAPI(formData.cmsEffectiveStartDate) ?? this.dateService.transformPOSTDate(new Date())!,
      cmsEffectiveEndDate: formatDateForAPI(formData.cmsEffectiveEndDate) ?? null,
      productId: this.filters.productId,
      tenantId: this.filters.tenantId,
      overrideTypeId: this.filters.overrideTypeId,
      planEffectiveStartDate: formatDateForAPI(formData.planEffectiveStartDate) ?? this.dateService.transformPOSTDate(new Date())!,
      planEffectiveEndDate: formatDateForAPI(formData.planEffectiveEndDate) ?? null,
      dmeposCategoryId: formData.dmeposCategory || null,
      hcpcsClinicalForms: clinicalForms,
      hcpcsModifiers: modifiers
    };
    
    this.codeManagementApiService.saveHcpcsWithAssociation(request).subscribe({
      next: (response) => {
        this.isSaving = false;
        
        if (response.result === SaveHcpcsResult.Updated) {
          this.messageService.add({
            severity: 'success',
            summary: 'Code Saved',
            detail: 'Code was successfully saved'
          });
          
          this.updateCodeDescriptionAndCategory(formData);
          this.loadModifiersData(true);
          this.isEditMode = false;
          this.initialCodeInfoData = null;          
          
        } else if (response.result === SaveHcpcsResult.Failed) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Unable to Save',
            detail: response.errorMessage || 'Failed to save code.'
          });
        }
      },
      error: () => {
        this.isSaving = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Something went wrong. Please try again or contact Portal support.'
        });
      }
    });
  }

  private updateCodeDescriptionAndCategory(formData: any): void {
    this.code.shortDescription = formData.shortDescription;
    const categoryOption = this.codeInfoComponent?.dmeposCategoryOptions?.find(
      opt => opt.value === formData.dmeposCategory
    );
    this.code.dmeposCategory = categoryOption?.label || formData.dmeposCategory;
  }
  
  private refreshExpandedCodeGrid(associations: any): void {
    this.code.planEffectiveStartDate = associations.planEffectiveStartDate || null;
    this.code.planEffectiveEndDate = associations.planEffectiveEndDate || null;
    
    const modifiers = associations.hcpcsModifiers || [];
    if (modifiers.length > 0) {
      const modifierNames = modifiers
        .map((m: any) => m.modifier)
        .filter((value: any, index: number, self: any[]) => self.indexOf(value) === index)
        .join(', ');
      this.code.modifiers = modifierNames;
    } else {
      this.code.modifiers = null;
    }
    
    const forms = associations.hcpcsClinicalForms || [];
    this.code.forms = forms.length;
    this.code.formDetails = forms.map((form: any) => ({
      formId: form.hcpcsToFormId || '',
      formioId: form.formIoId || '',
      formTitle: form.formIoTitle || ''
    }));
  }

  private parseDateValue(value: any): Date | null {
    return value ? this.dateService.parseDate(value) : null;
  }
}
